import React, { useMemo } from "react";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Flower, Cigarette, Cookie, Beaker, Droplet, Container, Sparkles, Tag, X } from "lucide-react";

// Friendly, mobile-first filter strip for the dispensary detail page.
// Strips out the inert cross-shop filters (distance, delivery, live menu)
// from the global FiltersBar and reorganizes the rest into one horizontal
// chip rail + two compact sliders, so a customer browsing one shop's menu
// can find what they want with one tap instead of scrolling a sidebar.

const CATEGORIES = [
  { key: "flower",      label: "Flower",      Icon: Flower },
  { key: "vape",        label: "Vape",        Icon: Cigarette },
  { key: "concentrate", label: "Concentrate", Icon: Beaker },
  { key: "edible",      label: "Edible",      Icon: Cookie },
  { key: "tincture",    label: "Tincture",    Icon: Droplet },
  { key: "topical",     label: "Topical",     Icon: Container },
];
const TYPES = [
  { key: "sativa", label: "Sativa", emoji: "☀️" },
  { key: "hybrid", label: "Hybrid", emoji: "⚖️" },
  { key: "indica", label: "Indica", emoji: "🌙" },
];

// Normalize raw product category values to the canonical filter key set.
// Cresco's API ships singular ("vape") + plural ("vaporizers"), Trulieve
// likes "Concentrates" capitalized, etc — collapse them all so the chip
// counts come out right.
const CATEGORY_ALIASES = {
  flower: "flower",
  vape: "vape",
  vapes: "vape",
  vaporizer: "vape",
  vaporizers: "vape",
  concentrate: "concentrate",
  concentrates: "concentrate",
  edible: "edible",
  edibles: "edible",
  tincture: "tincture",
  tinctures: "tincture",
  topical: "topical",
  topicals: "topical",
};

export const DispensaryMenuFilters = ({ menu = [], filters, setFilters, onReset, total, matched }) => {
  // Build live counts off the unfiltered menu so we know which chips would
  // actually do something. Hides Sativa/Indica at shops where the data
  // source (Cresco for Sunnyside) doesn't expose strain_type — no more
  // "the Sativa button does nothing" reports.
  const { catCounts, typeCounts, brandChoices } = useMemo(() => {
    const c = {};
    const t = {};
    const b = {};
    for (const p of menu) {
      const cat = CATEGORY_ALIASES[(p.category || "").toLowerCase()] || (p.category || "").toLowerCase();
      if (cat) c[cat] = (c[cat] || 0) + 1;
      const ty = (p.strain_type || "").toLowerCase();
      if (ty) t[ty] = (t[ty] || 0) + 1;
      const br = (p.brand || "").trim();
      if (br) b[br] = (b[br] || 0) + 1;
    }
    const brandList = Object.entries(b)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }));
    return { catCounts: c, typeCounts: t, brandChoices: brandList };
  }, [menu]);

  const visibleCategories = CATEGORIES.filter(({ key }) => (catCounts[key] || 0) > 0);
  const visibleTypes = TYPES.filter(({ key }) => (typeCounts[key] || 0) > 0);

  const activeCount =
    (filters.category ? 1 : 0) +
    (filters.type ? 1 : 0) +
    (filters.brand ? 1 : 0) +
    (filters.thc_min ? 1 : 0) +
    (filters.price_max && filters.price_max < 200 ? 1 : 0) +
    (filters.in_stock_only ? 1 : 0);

  const toggle = (key, val) =>
    setFilters((f) => ({ ...f, [key]: f[key] === val ? "" : val }));

  return (
    <div
      data-testid="disp-menu-filters"
      className="border border-border bg-card rounded-2xl p-4 sm:p-5 space-y-4 mb-6"
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-serif text-lg sm:text-xl">
            <span className="text-emerald-300">{matched}</span>
            <span className="text-foreground/50"> of {total}</span>
          </div>
          <div className="text-[11px] uppercase tracking-wider text-foreground/50">
            products matching your filters
          </div>
        </div>
        {activeCount > 0 && (
          <button
            data-testid="disp-menu-filters-reset"
            onClick={onReset}
            className="inline-flex items-center gap-1 text-xs text-foreground/70 hover:text-foreground border border-border rounded-full px-3 h-8"
          >
            <X className="w-3 h-3" />
            Clear ({activeCount})
          </button>
        )}
      </div>

      {/* Category icons — only show categories that actually exist in this
          shop's menu. Hides the whole row if the shop has only one category. */}
      {visibleCategories.length > 1 && (
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${Math.min(visibleCategories.length, 4)}, minmax(0, 1fr))` }}
        >
          {visibleCategories.map(({ key, label, Icon }) => {
            const active = filters.category === key;
            const count = catCounts[key] || 0;
            return (
              <button
                key={key}
                data-testid={`disp-filter-category-${key}`}
                onClick={() => toggle("category", key)}
                className={`flex flex-col items-center justify-center gap-1.5 rounded-xl px-2 py-3 transition-all border ${
                  active
                    ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-100"
                    : "bg-background border-border text-foreground/80 hover:border-foreground/40"
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs font-medium">{label}</span>
                <span className="text-[10px] text-foreground/50">{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Strain type pills — Cresco's API doesn't expose strain_type, so at
          Sunnyside shops every product comes through as hybrid. Hide chips
          for types with 0 matches so users never tap a button that does
          nothing. If only one type exists, hide the row entirely. */}
      {visibleTypes.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {visibleTypes.map(({ key, label, emoji }) => {
            const active = filters.type === key;
            const count = typeCounts[key] || 0;
            return (
              <button
                key={key}
                data-testid={`disp-filter-type-${key}`}
                onClick={() => toggle("type", key)}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-sm border transition ${
                  active
                    ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-100"
                    : "bg-background border-border text-foreground/80 hover:border-foreground/40"
                }`}
              >
                <span aria-hidden="true">{emoji}</span>
                {label}
                <span className="text-[10px] text-foreground/50">{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Brand picker — only appears when this shop actually stocks brands
          the ingest layer could identify. Compact so the row stays one-tap. */}
      {brandChoices.length > 0 && (
        <div className="flex items-center gap-2">
          <label
            htmlFor="disp-filter-brand"
            className="flex items-center gap-1.5 text-xs text-foreground/80 shrink-0"
          >
            <Tag className="w-3.5 h-3.5 text-emerald-300" strokeWidth={2} />
            Brand
          </label>
          <Select
            value={filters.brand || "__all__"}
            onValueChange={(v) =>
              setFilters((f) => ({ ...f, brand: v === "__all__" ? "" : v }))
            }
          >
            <SelectTrigger
              id="disp-filter-brand"
              data-testid="disp-filter-brand"
              className="h-9 rounded-full bg-background border-border text-sm"
            >
              <SelectValue placeholder="All brands" />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="__all__">All brands</SelectItem>
              {brandChoices.map(({ name, count }) => (
                <SelectItem key={name} value={name}>
                  <span className="flex items-center gap-2">
                    {name}
                    <span className="text-[10px] text-foreground/50 tabular-nums">
                      {count}
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Sliders + toggle */}
      <div className="grid sm:grid-cols-2 gap-4 pt-1">
        <div>
          <div className="flex justify-between text-xs mb-2">
            <span className="text-foreground/80 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-emerald-300" />
              Minimum THC
            </span>
            <span className="text-emerald-300 font-medium">
              {filters.thc_min || 0}%+
            </span>
          </div>
          <Slider
            data-testid="disp-filter-thc"
            value={[filters.thc_min || 0]}
            onValueChange={([v]) => setFilters((f) => ({ ...f, thc_min: v }))}
            max={30}
            step={1}
          />
        </div>
        <div>
          <div className="flex justify-between text-xs mb-2">
            <span className="text-foreground/80">Max price</span>
            <span className="text-emerald-300 font-medium">
              ${filters.price_max || 200}
            </span>
          </div>
          <Slider
            data-testid="disp-filter-price"
            value={[filters.price_max || 200]}
            onValueChange={([v]) =>
              setFilters((f) => ({ ...f, price_max: v }))
            }
            min={10}
            max={200}
            step={5}
          />
        </div>
      </div>

      <label className="flex items-center gap-2.5 text-sm cursor-pointer pt-1 select-none">
        <input
          type="checkbox"
          data-testid="disp-filter-in-stock"
          checked={!!filters.in_stock_only}
          onChange={(e) =>
            setFilters((f) => ({ ...f, in_stock_only: e.target.checked }))
          }
          className="accent-emerald-500 w-4 h-4"
        />
        <span className="text-foreground/85">In stock right now only</span>
      </label>
    </div>
  );
};
